import { type ZodType } from 'zod';
import {
  RussiaRequestSchema,
  type RefuseRussiaRequestBody,
  type ReopenRussiaRequestBody,
  type ReturnRussiaRequestBody,
  type RussiaRequest,
  type RussiaRequestTransitionKey,
  type SuccessRussiaRequestBody,
} from '@magimp/shared';

import { request } from '../client.js';

/**
 * @aiPurpose POST /russia-requests/:id/transitions/:key wrapper. The
 *   transitions that carry a body are `return` / `reopen` (`{ reason? }`),
 *   `success` (`{ bidId? }`), and `refuse` (`{ reason, comment? }`). The
 *   wire schemas keep optional fields optional at the Zod layer so a
 *   missing required field surfaces the typed domain error from the
 *   service — mirror of the China-side `TransitionBody` semantics.
 */
export type RussiaTransitionBody =
  | ReturnRussiaRequestBody
  | ReopenRussiaRequestBody
  | SuccessRussiaRequestBody
  | RefuseRussiaRequestBody;

export function transitionRussiaRequest(
  id: string,
  key: RussiaRequestTransitionKey,
  body?: RussiaTransitionBody,
): Promise<RussiaRequest> {
  const init: RequestInit = { method: 'POST' };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
    init.headers = { 'content-type': 'application/json' };
  }
  return request<RussiaRequest>(
    `/russia-requests/${encodeURIComponent(id)}/transitions/${key}`,
    init,
    {},
    RussiaRequestSchema as unknown as ZodType<RussiaRequest>,
  );
}

/**
 * @aiPurpose POST /russia-requests/:id/winner — manager-side winner-bid
 *   selection (#1544). Returns 204 (no body). The manager picks the
 *   winner before finalising via `success`.
 */
export function pickRussiaRequestWinner(id: string, bidId: string): Promise<void> {
  return request<void>(`/russia-requests/${encodeURIComponent(id)}/winner`, {
    method: 'POST',
    body: JSON.stringify({ bidId }),
    headers: { 'content-type': 'application/json' },
  });
}
